import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Braces, Code2, Globe, MapPin, Search, Eye, FileJson, Shield,
  Sparkles, Trophy, ScrollText, Layers, Loader2, CheckCircle2,
  Network, Database, Copy, Download, Wand2, Gift, Crown, Zap,
  ArrowDown, MinusCircle, ScanLine, AlertCircle, XCircle,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Location, CitationOrder } from "@shared/schema";

const PATENTS = [
  {
    id: "us10769351b2",
    name: "Rendering Based on a Document Object Model",
    code: "US10769351B2",
    summary:
      "Google builds a DOM from a page's markup using an off-screen browser before deciding what content actually counts. If your structured data isn't in the rendered DOM, it doesn't exist to the ranker.",
  },
  {
    id: "us8356247b2",
    name: "Hidden Text Data Indexing",
    code: "US8356247B2",
    summary:
      "Generates a render tree from the DOM to identify which text is visible to a real user. Schema.org JSON-LD is the cleanest, machine-readable signal that confirms what the visible content is actually about.",
  },
  {
    id: "us11489845b1",
    name: "Speculative Rendering",
    code: "US11489845B1",
    summary:
      "A surrogate browser processes the DOM and ships a lower-level data structure to the client. Schema markup survives that compression because it's already structured — your name, address, hours, and category arrive intact.",
  },
  {
    id: "us9391832b1",
    name: "Secure Surrogate Cloud Browsing",
    code: "US9391832B1",
    summary:
      "A remote browser executes events and returns DOM layout updates. Schema gives Google a stable, declarative source of truth that survives every layout change and theme update on your site.",
  },
  {
    id: "us9141722b2",
    name: "Prerendering Network Content",
    code: "US9141722B2",
    summary:
      "Content is rendered in a hidden browser instance before display. JSON-LD schema is parsed during that prerender step, so it influences how Google understands your page before a single human eye lands on it.",
  },
];

const PACKAGE_LABELS: Record<string, string> = {
  basic_schema: "Basic Schema",
  super_schema: "Super Schema",
};

// Restaurants get schema.org/Restaurant; everything else uses LocalBusiness.
// We match against the Google Business Profile primary category text so it
// stays in sync with whatever is on the live GBP listing.
function detectSchemaType(category: string | null | undefined): "Restaurant" | "LocalBusiness" {
  const c = (category || "").toLowerCase();
  const restaurantKeywords = [
    "restaurant", "cafe", "café", "bistro", "diner", "steakhouse",
    "pizzeria", "pizza place", "eatery", "food truck", "barbecue", "bbq",
    "fast food", "buffet", "sushi", "ramen", "noodle", "taco",
    "sandwich shop", "bakery", "brewpub", "gastropub", "deli",
    "ice cream shop", "donut shop", "doughnut shop", "coffee shop",
    "tea house", "bar & grill", "bar and grill", "tavern", "pub",
    "brewery", "winery", "juice bar", "creperie", "crêperie",
    "ramen shop", "burger", "chicken restaurant", "seafood restaurant",
    "thai restaurant", "indian restaurant", "mexican restaurant",
    "italian restaurant", "chinese restaurant", "japanese restaurant",
    "korean restaurant", "vietnamese restaurant", "vegan restaurant",
    "vegetarian restaurant", "breakfast restaurant", "brunch", "caterer",
  ];
  if (restaurantKeywords.some((kw) => c.includes(kw))) return "Restaurant";
  return "LocalBusiness";
}

// Strict absolute https/http URL check. Used so we never emit malformed values
// in `url`, `@id`, or `sameAs` — bad URLs cause Google to reject the markup.
function isValidUrl(value: string | null | undefined): value is string {
  if (!value || typeof value !== "string") return false;
  try {
    const u = new URL(value.trim());
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

// Geo coords are valid only when both lat & lng parse as finite numbers within
// the actual lat/lng range AND are not the (0, 0) sentinel some seeders use.
function parseCoord(value: unknown, kind: "lat" | "lng"): number | null {
  const n = typeof value === "number" ? value : Number(value ?? NaN);
  if (!Number.isFinite(n) || n === 0) return null;
  if (kind === "lat" && (n < -90 || n > 90)) return null;
  if (kind === "lng" && (n < -180 || n > 180)) return null;
  return n;
}

// Build a Restaurant or LocalBusiness JSON-LD object from a Location row plus
// its connected social profiles. Only fields we have real data for are emitted
// — anything missing is dropped so the output mirrors the GBP listing exactly.
function buildLocationJsonLd(
  location: Location,
  socialProfiles: Record<string, string> = {},
) {
  const schemaType = detectSchemaType(location.category);

  // sameAs collects the user's verified online identities. We pull from the
  // social profiles they've connected on Social Fortress, then add the Google
  // Maps URL for the listing so Google knows this schema entity is the same
  // entity it already has on the GBP. Drop anything that isn't a valid URL.
  const sameAs: string[] = [];
  for (const url of Object.values(socialProfiles)) {
    if (typeof url === "string" && isValidUrl(url.trim())) sameAs.push(url.trim());
  }
  if (location.gbpPlaceId) {
    const gmaps = `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(location.gbpPlaceId)}`;
    if (isValidUrl(gmaps)) sameAs.push(gmaps);
  }

  // Only emit aggregateRating when we actually have reviews; otherwise Google
  // will flag it as invalid markup.
  const totalReviews = Number(location.totalReviews ?? 0);
  const avgRating = Number(location.avgRating ?? 0);
  const aggregateRating =
    totalReviews > 0 && avgRating > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: Number(avgRating.toFixed(1)),
          reviewCount: totalReviews,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;

  // Require BOTH lat AND lng to be valid coords; emitting a half-pair is worse
  // than emitting none.
  const lat = parseCoord(location.lat, "lat");
  const lng = parseCoord(location.lng, "lng");
  const hasGeo = lat !== null && lng !== null;

  // Only build url/@id if the website is an actual http(s) URL.
  const websiteUrl = isValidUrl(location.website) ? location.website!.trim() : undefined;
  const idUrl = websiteUrl ? `${websiteUrl.replace(/\/$/, "")}/#localbusiness` : undefined;

  // Logo image. The product forge persists the detected logo to location.logoUrl
  // as a path like "/uploads/brand-logo-X.png". Schema.org requires an absolute
  // URL, so we anchor it to the app's origin where the file is actually served.
  let imageUrl: string | undefined;
  const rawLogo: string = (typeof location.logoUrl === "string" ? location.logoUrl : "").trim();
  if (rawLogo.length > 0) {
    if (/^https?:\/\//i.test(rawLogo)) {
      imageUrl = isValidUrl(rawLogo) ? rawLogo : undefined;
    } else if (rawLogo.startsWith("/") && typeof window !== "undefined") {
      const candidate = `${window.location.origin}${rawLogo}`;
      imageUrl = isValidUrl(candidate) ? candidate : undefined;
    }
  }

  const obj: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": schemaType,
    "@id": idUrl,
    name: location.name || undefined,
    url: websiteUrl,
    image: imageUrl,
    telephone: location.phone || undefined,
    priceRange: "$",
    address: (location.address || location.city || location.state || location.zip)
      ? {
          "@type": "PostalAddress",
          streetAddress: location.address || undefined,
          addressLocality: location.city || undefined,
          addressRegion: location.state || undefined,
          postalCode: location.zip || undefined,
          addressCountry: "US",
        }
      : undefined,
    geo: hasGeo
      ? {
          "@type": "GeoCoordinates",
          latitude: lat,
          longitude: lng,
        }
      : undefined,
    aggregateRating,
    sameAs: sameAs.length ? sameAs : undefined,
  };

  // Strip undefined and empty inner objects so the output is tight.
  const clean = (o: any): any => {
    if (Array.isArray(o)) return o.map(clean).filter((v) => v !== undefined);
    if (o && typeof o === "object") {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(o)) {
        const cv = clean(v);
        if (cv !== undefined && !(typeof cv === "object" && !Array.isArray(cv) && Object.keys(cv).length === 0)) {
          out[k] = cv;
        }
      }
      return out;
    }
    return o;
  };

  return clean(obj);
}

export default function SchemaPage() {
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations[0]?.id || "";

  const { data: orders = [] } = useQuery<CitationOrder[]>({
    queryKey: ["/api/citation-orders", locationId],
    enabled: !!locationId,
  });

  const superSchemaOrder = orders.find(
    (o) => o.packageType === "super_schema" && o.status === "paid",
  );

  const formatInstallDate = (date: Date | string | null | undefined) => {
    if (!date) return null;
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };
  const superInstalledOn = formatInstallDate(superSchemaOrder?.installedAt);

  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    apiRequest("GET", `/api/citation-orders/verify-session?session_id=${sessionId}`)
      .then((r) => r.json())
      .then((order) => {
        if (order?.status === "paid") {
          const label = PACKAGE_LABELS[order.packageType] || "Your order";
          toast({
            title: "Payment confirmed",
            description: `${label} is queued for install. We'll email you when it's live.`,
          });
          queryClient.invalidateQueries({ queryKey: ["/api/citation-orders", locationId] });
        }
        window.history.replaceState({}, "", "/schema");
      })
      .catch(() => {
        window.history.replaceState({}, "", "/schema");
      });
  }, [sessionId, locationId, toast]);

  const checkout = useMutation({
    mutationFn: async (packageType: "super_schema") => {
      if (!locationId) throw new Error("Pick a location first");
      const res = await apiRequest("POST", "/api/citation-orders/checkout", {
        locationId,
        packageType,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: "Checkout failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const pendingPackage = checkout.isPending ? (checkout.variables as string | undefined) : undefined;
  const isSuperInstalled = !!superSchemaOrder;
  const superBuyDisabled = !locationId || isSuperInstalled || checkout.isPending;
  const activeLocation = locations.find((l) => l.id === locationId);

  // ── Free LocalBusiness / Restaurant schema generator ────────────────────────
  // Pull the user's connected social profiles for the active location so we
  // can build sameAs from data they already manage on Social Fortress.
  const { data: socialProfiles = {} } = useQuery<Record<string, string>>({
    queryKey: ["/api/locations", locationId, "social-profiles"],
    queryFn: async () => {
      if (!locationId) return {};
      const res = await apiRequest(
        "GET",
        `/api/locations/${locationId}/social-profiles`,
      );
      return res.json();
    },
    enabled: !!locationId,
  });

  const generatedJsonLd = useMemo(
    () => (activeLocation ? buildLocationJsonLd(activeLocation, socialProfiles) : null),
    [activeLocation, socialProfiles],
  );

  // ── GBP landing-page schema scanner ────────────────────────────────────────
  // Hits /api/schema/scan/:locationId which fetches the site and looks for
  // <script type="application/ld+json"> blocks.
  type SchemaScanResult = {
    url: string;
    finalUrl: string;
    httpStatus: number;
    ok: boolean;
    hasSchema: boolean;
    blockCount?: number;
    validBlockCount?: number;
    types?: string[];
    microdataTypes?: string[];
    blocks?: Array<{ valid: boolean; types: string[]; error?: string; preview: string }>;
    message?: string;
    botWall?: boolean;
    tlsBypassed?: boolean;
    // Set when our direct fetch was blocked and we used Brave + the
    // Schema.org validator to read the page instead.
    source?: "direct" | "validator";
    braveConfirmed?: boolean;
    braveIndexedUrl?: string;
    validatorErrors?: number;
    validatorWarnings?: number;
  };
  const [scanResult, setScanResult] = useState<SchemaScanResult | null>(null);
  useEffect(() => {
    setScanResult(null);
  }, [locationId]);

  const scanSchema = useMutation<SchemaScanResult, Error, void>({
    mutationFn: async () => {
      if (!locationId) throw new Error("Pick a location first");
      const res = await apiRequest("GET", `/api/schema/scan/${locationId}`);
      return res.json();
    },
    onSuccess: (data) => {
      setScanResult(data);
      if (data.hasSchema && data.source === "validator") {
        toast({
          title: "Schema detected (via validator)",
          description: `Direct fetch was blocked, but the Schema.org validator (verified via Brave) found ${data.blockCount ?? 0} structured-data object(s).`,
        });
      } else if (data.hasSchema) {
        toast({
          title: "Schema detected",
          description: `${data.validBlockCount ?? data.blockCount ?? 0} JSON-LD block(s) found on your GBP landing page.`,
        });
      } else if (data.botWall && data.source === "validator") {
        toast({
          title: "No schema found (via validator)",
          description: "Direct fetch was blocked. We confirmed the URL with Brave and asked the validator to read it — no structured data was found.",
        });
      } else if (data.botWall) {
        toast({
          title: "Site is behind a bot wall",
          description: "We got the verification page, not the real site. We can't tell what schema is installed.",
          variant: "destructive",
        });
      } else if (data.ok) {
        toast({
          title: "No schema found",
          description: "We reached the page but couldn't find any JSON-LD schema. Generate one below and install it.",
        });
      }
    },
    onError: (err) => {
      toast({ title: "Scan failed", description: err.message, variant: "destructive" });
    },
  });

  const generatedJsonString = useMemo(
    () => (generatedJsonLd ? JSON.stringify(generatedJsonLd, null, 2) : ""),
    [generatedJsonLd],
  );

  // Wrap the JSON in a <script> tag for paste-into-head copy. Even though all
  // values come from our own DB rather than user input, we still escape any
  // `</script>` token defensively so the wrapper can never be broken out of.
  const generatedScriptTag = useMemo(
    () =>
      generatedJsonLd
        ? `<script type="application/ld+json">\n${generatedJsonString.replace(/<\/script>/gi, "<\\/script>")}\n</script>`
        : "",
    [generatedJsonLd, generatedJsonString],
  );

  const canGenerate = !!activeLocation;
  const detectedSchemaType = activeLocation
    ? detectSchemaType(activeLocation.category)
    : null;
  const sameAsCount = (generatedJsonLd?.sameAs as string[] | undefined)?.length || 0;
  const hasAggregateRating = !!generatedJsonLd?.aggregateRating;
  const hasGeo = !!generatedJsonLd?.geo;
  const imageUrl = (generatedJsonLd?.image as string | undefined) || undefined;

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast({ title: "Copied", description: `${label} copied to clipboard.` }),
      () => toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" }),
    );
  };

  const downloadSnippet = () => {
    const blob = new Blob([generatedScriptTag], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = (activeLocation?.name || "localbusiness").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    a.download = `${safeName}-schema.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container max-w-6xl py-8 px-6 md:px-10 lg:px-12 space-y-10 overflow-y-auto h-full pb-24" data-testid="container-schema">
      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 flex items-center justify-center">
            <Braces className="w-5 h-5 text-purple-500" />
          </div>
          <h1 className="text-3xl font-bold" data-testid="text-schema-title">Schema Markup</h1>
        </div>
        <p className="text-muted-foreground max-w-3xl" data-testid="text-schema-description">
          The third pillar of local ranking. Your website tells Google what you do, your Google Business Profile tells
          Google where you are — schema is the structured handshake that connects them and confirms you are who you say
          you are.
        </p>
      </header>

      {/* Three pillars */}
      <Card className="border-2 border-purple-500/30 bg-gradient-to-br from-background via-background to-purple-500/5">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Globe, label: "Website", body: "What you sell, how you talk about it, what makes you different." },
              { icon: MapPin, label: "Google Business Profile", body: "Where you are, your hours, your reviews, your photos, your category." },
              { icon: Braces, label: "Schema Markup", body: "Structured JSON-LD that ties the website and the GBP into one verified entity." },
            ].map(({ icon: Icon, label, body }, i) => (
              <div
                key={label}
                className="flex flex-col gap-3 p-4 rounded-lg border bg-card"
                data-testid={`card-pillar-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="font-semibold">{label}</div>
                </div>
                <p className="text-sm text-muted-foreground">{body}</p>
                {i < 2 && (
                  <div className="hidden md:flex absolute" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 p-4 rounded-lg bg-purple-500/10 border border-purple-500/30 text-sm">
            <strong className="text-purple-600 dark:text-purple-400">Google's own patents</strong>{" "}
            describe how the rendered DOM — including embedded structured data — is what gets indexed and used to
            judge relevance. Skip schema and you're betting on Google guessing right. Add schema and you're handing it
            the answer in machine-readable form.
          </div>
        </CardContent>
      </Card>

      {/* THE REAL FIX — Super Schema (hero offer) */}
      <div className="relative" data-testid="wrapper-super-schema-hero">
        {/* Recommended ribbon */}
        <div className="absolute -top-3 left-6 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-purple-500/30">
          <Crown className="w-3.5 h-3.5" />
          Recommended · the real fix
        </div>
        <Card
          id="super-schema-card"
          className="border-2 border-purple-500 bg-gradient-to-br from-purple-500/10 via-background to-fuchsia-500/10 shadow-2xl shadow-purple-500/20 ring-4 ring-purple-500/10"
          data-testid="card-super-schema"
        >
          <CardHeader className="pt-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-fuchsia-600 flex items-center justify-center shadow-xl shadow-purple-500/30">
                  <Network className="w-8 h-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-bold" data-testid="text-super-schema-title">
                    Super Schema
                  </CardTitle>
                  <CardDescription className="text-base mt-1.5">
                    The full <strong className="text-foreground">eight-entity knowledge graph</strong>, hand-built by us
                    and installed on your site. This is what Google's patents are actually describing — one cohesive
                    machine-readable identity, not a single disconnected block.
                  </CardDescription>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 to-fuchsia-600 bg-clip-text text-transparent" data-testid="text-super-schema-price">
                  $999
                </div>
                <div className="text-xs text-muted-foreground">one-time · per location</div>
                <div className="text-[11px] text-muted-foreground mt-1">Done for you · we install it</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Why this beats the free option */}
            <div className="rounded-lg bg-purple-500/10 border border-purple-500/30 p-4 space-y-2 text-sm" data-testid="panel-super-vs-basic">
              <div className="flex items-center gap-2 font-semibold text-purple-700 dark:text-purple-300">
                <Zap className="w-4 h-4" />
                Why this beats the free generator
              </div>
              <ul className="space-y-1.5 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong>8 entity types vs 1.</strong> The free generator gives you LocalBusiness. We give you all eight, linked through <code className="text-xs">@graph</code> so they reinforce each other.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong>Done for you.</strong> No copy-paste, no editing your theme files, no breaking your site. Our team installs it and confirms it validates.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  <span><strong>Built for rich results.</strong> FAQ snippets, sitelinks, star ratings, image cards — schema types Google actually rewards in the SERP.</span>
                </li>
              </ul>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Network className="w-4 h-4 text-purple-500" />
                What you get — all 8 entity types, fully interlinked:
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  { label: "WebPage", body: "Per-page entity that anchors every URL into the graph." },
                  { label: "WebSite", body: "Site-level identity with sitelink search and publisher metadata." },
                  { label: "Organization", body: "Brand-level entity tying NAP, logo, and social profiles together." },
                  { label: "LocalBusiness", body: "Geo-anchored entity with hours, category, and address." },
                  { label: "Service", body: "Each service you offer modeled with description and area served." },
                  { label: "FAQ", body: "Q&A pairs eligible for FAQ rich results in the SERP." },
                  { label: "ImageObject", body: "Hero and gallery images marked up with caption and creator." },
                  { label: "Person", body: "Owner / practitioner entity for E-E-A-T signal reinforcement." },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                    <span><strong>{item.label}</strong> — {item.body}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-2 border-t border-purple-500/20">
              <Select value={locationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger className="sm:max-w-sm" data-testid="select-location-super-schema">
                  <SelectValue placeholder="Choose a location to install schema on" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`option-super-location-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => checkout.mutate("super_schema")}
                disabled={superBuyDisabled}
                size="lg"
                className="bg-brand-orange hover:bg-brand-orange/90 text-white shadow-lg shadow-brand-orange/30 text-base font-semibold"
                data-testid="button-buy-super-schema"
              >
                {pendingPackage === "super_schema" ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Crown className="w-5 h-5 mr-2" />
                )}
                {isSuperInstalled
                  ? superInstalledOn ? "Already Installed" : "Queued for Install"
                  : "Get the real fix — $999"}
              </Button>

              {isSuperInstalled && (
                <Badge
                  className="bg-green-500/15 text-green-600 border-green-500/30 px-3 py-1.5"
                  data-testid="badge-super-schema-installed"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {superInstalledOn ? `Installed on ${superInstalledOn}` : "Queued for install"}
                </Badge>
              )}
            </div>

            {!locationId && (
              <p className="text-xs text-muted-foreground -mt-3" data-testid="hint-select-location-super">
                Pick a location above to enable the buy button.
              </p>
            )}

            {isSuperInstalled && !superInstalledOn && (
              <p className="text-xs text-muted-foreground -mt-3" data-testid="text-super-schema-status">
                Payment confirmed. Our team is building your full entity-graph schema — we'll email you when it's live.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* "Settle for the bare minimum" framing divider */}
      <div className="flex items-center gap-3 max-w-3xl mx-auto pt-4" data-testid="divider-settle">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
          <MinusCircle className="w-3.5 h-3.5" />
          Or settle for the bare minimum
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Basic Schema — Free LocalBusiness JSON-LD generator (DIY route, demoted) */}
      <Card
        id="basic-schema-generator"
        className="border border-border/60 bg-muted/10"
        data-testid="card-basic-schema"
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Braces className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-lg" data-testid="text-basic-schema-title">
                    Basic Schema Generator
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground" data-testid="badge-basic-schema-diy">
                    DIY · the bare minimum
                  </Badge>
                </div>
                <CardDescription className="mt-1">
                  Just the LocalBusiness block, generated from your GBP. You paste it into your site yourself. It works —
                  but it's <strong>1 of the 8 entity types</strong> Google looks for. The other 7 (Organization, Service,
                  FAQ, ImageObject, Person, WebPage, WebSite) are what Super Schema adds and ties together with{" "}
                  <code className="text-xs">@graph</code>.
                </CardDescription>
              </div>
            </div>
            <div className="text-right shrink-0">
              <Badge
                variant="outline"
                className="text-xs text-muted-foreground"
                data-testid="badge-basic-schema-free"
              >
                <Gift className="w-3 h-3 mr-1.5" />
                Free
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Location picker — required source of truth */}
          <div className="space-y-3" data-testid="panel-location-source">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <Wand2 className="w-4 h-4 text-purple-500 shrink-0" />
              <span className="text-sm text-muted-foreground">
                Generating schema for:
              </span>
              {locations.length > 0 ? (
                <Select value={locationId} onValueChange={setSelectedLocationId}>
                  <SelectTrigger className="sm:max-w-md" data-testid="select-location-schema">
                    <SelectValue placeholder="Choose a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`option-location-${l.id}`}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm font-medium">No locations connected yet</span>
              )}
            </div>

            {activeLocation ? (
              <div
                className="rounded-lg border bg-muted/30 p-4 space-y-3"
                data-testid="panel-location-summary"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-semibold" data-testid="text-detected-name">
                      {activeLocation.name}
                    </span>
                  </div>
                  <Badge
                    className={
                      detectedSchemaType === "Restaurant"
                        ? "bg-orange-500/15 text-orange-500 border-orange-500/30"
                        : "bg-purple-500/15 text-purple-500 border-purple-500/30"
                    }
                    data-testid="badge-detected-type"
                  >
                    @type: {detectedSchemaType}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5" data-testid="row-source-category">
                    <span className="font-medium text-foreground/70">Category:</span>
                    <span>{activeLocation.category || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-website">
                    <span className="font-medium text-foreground/70">Website:</span>
                    <span className="truncate">{activeLocation.website || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-phone">
                    <span className="font-medium text-foreground/70">Phone:</span>
                    <span>{activeLocation.phone || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-address">
                    <span className="font-medium text-foreground/70">Address:</span>
                    <span className="truncate">
                      {[activeLocation.address, activeLocation.city, activeLocation.state, activeLocation.zip]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-geo">
                    <span className="font-medium text-foreground/70">Coordinates:</span>
                    <span>{hasGeo ? `${activeLocation.lat}, ${activeLocation.lng}` : "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-rating">
                    <span className="font-medium text-foreground/70">Reviews:</span>
                    <span>
                      {hasAggregateRating
                        ? `${activeLocation.avgRating}★ from ${activeLocation.totalReviews}`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-pricerange">
                    <span className="font-medium text-foreground/70">Price range:</span>
                    <span>$ (default)</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="row-source-logo">
                    <span className="font-medium text-foreground/70">Logo:</span>
                    {imageUrl ? (
                      <a
                        href={imageUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-primary hover:underline"
                        data-testid="link-logo-image"
                      >
                        {imageUrl.split("/").pop()}
                      </a>
                    ) : (
                      <span>— (run Product Forge to detect)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 sm:col-span-2" data-testid="row-source-sameas">
                    <span className="font-medium text-foreground/70">sameAs links:</span>
                    <span>
                      {sameAsCount > 0
                        ? `${sameAsCount} (social profiles${activeLocation.gbpPlaceId ? " + Google Maps" : ""})`
                        : "—"}
                    </span>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground italic pt-1 border-t border-border/50">
                  Pulled directly from your Google Business Profile and Social Fortress.
                  Update the location to refresh this schema.
                </p>

                {/* GBP landing-page schema scanner */}
                <div
                  className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-4 space-y-3"
                  data-testid="panel-schema-scan"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        <ScanLine className="w-4 h-4 text-purple-500" />
                        Check your GBP landing page
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We'll fetch{" "}
                        <span className="font-mono text-foreground/80">
                          {activeLocation.website || "(no website on file)"}
                        </span>{" "}
                        and look for installed schema (JSON-LD).
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => scanSchema.mutate()}
                      disabled={!activeLocation.website || scanSchema.isPending}
                      className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                      data-testid="button-scan-schema"
                    >
                      {scanSchema.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ScanLine className="w-4 h-4 mr-2" />
                      )}
                      {scanSchema.isPending ? "Scanning…" : "Scan website"}
                    </Button>
                  </div>

                  {scanResult && (
                    <div
                      className="rounded-md border bg-background/60 p-3 space-y-3 text-xs"
                      data-testid="panel-schema-scan-result"
                    >
                      {/* Header verdict */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          {scanResult.hasSchema ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              <span className="font-semibold text-green-500" data-testid="text-scan-verdict">
                                Schema detected
                              </span>
                            </>
                          ) : scanResult.botWall ? (
                            <>
                              <Shield className="w-4 h-4 text-yellow-500" />
                              <span className="font-semibold text-yellow-500" data-testid="text-scan-verdict">
                                Blocked by bot verification
                              </span>
                            </>
                          ) : scanResult.ok ? (
                            <>
                              <XCircle className="w-4 h-4 text-orange-500" />
                              <span className="font-semibold text-orange-500" data-testid="text-scan-verdict">
                                No JSON-LD schema found
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-red-500" />
                              <span className="font-semibold text-red-500" data-testid="text-scan-verdict">
                                Couldn't reach the page
                              </span>
                            </>
                          )}
                          {scanResult.source === "validator" && (
                            <span
                              className="ml-1 inline-flex items-center rounded border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-400"
                              title={
                                scanResult.braveConfirmed
                                  ? "Direct fetch was blocked. URL confirmed via Brave Search; structured data read via Schema.org validator."
                                  : "Read via Schema.org validator (direct fetch was blocked)."
                              }
                              data-testid="badge-scan-source"
                            >
                              via validator
                            </span>
                          )}
                        </div>
                        <a
                          href={scanResult.finalUrl || scanResult.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-muted-foreground hover:text-foreground underline truncate max-w-[60%]"
                          data-testid="link-scan-final-url"
                        >
                          {scanResult.finalUrl || scanResult.url}
                        </a>
                      </div>

                      {scanResult.message && (
                        <p className="text-[11px] text-muted-foreground">{scanResult.message}</p>
                      )}

                      {/* Stats */}
                      {scanResult.ok && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="rounded border bg-muted/30 px-2 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">JSON-LD blocks</div>
                            <div className="text-sm font-semibold" data-testid="stat-block-count">
                              {scanResult.blockCount ?? 0}
                            </div>
                          </div>
                          <div className="rounded border bg-muted/30 px-2 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Valid JSON</div>
                            <div className="text-sm font-semibold" data-testid="stat-valid-count">
                              {scanResult.validBlockCount ?? 0}
                            </div>
                          </div>
                          <div className="rounded border bg-muted/30 px-2 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Unique types</div>
                            <div className="text-sm font-semibold" data-testid="stat-types-count">
                              {scanResult.types?.length ?? 0}
                            </div>
                          </div>
                          <div className="rounded border bg-muted/30 px-2 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">HTTP</div>
                            <div className="text-sm font-semibold" data-testid="stat-http-status">
                              {scanResult.httpStatus || "—"}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Detected @types */}
                      {!!scanResult.types?.length && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                            Detected @types
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {scanResult.types.map((t) => (
                              <Badge
                                key={t}
                                className="bg-green-500/15 text-green-500 border-green-500/30 text-[10px]"
                                data-testid={`badge-detected-type-${t}`}
                              >
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Microdata fallback */}
                      {!!scanResult.microdataTypes?.length && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                            Microdata itemtypes (legacy)
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {scanResult.microdataTypes.map((t) => (
                              <Badge
                                key={t}
                                variant="outline"
                                className="text-[10px]"
                                data-testid={`badge-microdata-type-${t}`}
                              >
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actionable hint */}
                      {scanResult.ok && !scanResult.hasSchema && !scanResult.botWall && (
                        <div className="flex items-start gap-2 rounded border border-orange-500/30 bg-orange-500/5 p-2">
                          <ArrowDown className="w-3.5 h-3.5 text-orange-500 mt-0.5 shrink-0" />
                          <p className="text-[11px] text-foreground/80">
                            No JSON-LD on this page. Generate the free LocalBusiness schema below and paste it
                            into your site's <span className="font-mono">&lt;head&gt;</span> — Google needs it
                            to tie your website and GBP into one verified entity.
                          </p>
                        </div>
                      )}

                      {/* Per-block parse errors */}
                      {scanResult.blocks?.some((b) => !b.valid) && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] uppercase tracking-wide text-red-500 font-semibold">
                            Invalid JSON-LD blocks
                          </div>
                          {scanResult.blocks
                            .filter((b) => !b.valid)
                            .map((b, i) => (
                              <div
                                key={i}
                                className="rounded border border-red-500/30 bg-red-500/5 p-2 space-y-1"
                                data-testid={`row-invalid-block-${i}`}
                              >
                                <div className="text-[11px] text-red-500">{b.error}</div>
                                <pre className="text-[10px] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all">
                                  {b.preview}
                                </pre>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="rounded-lg border border-dashed bg-muted/20 p-6 text-center space-y-2"
                data-testid="panel-no-location"
              >
                <Search className="w-6 h-6 text-muted-foreground mx-auto" />
                <p className="text-sm font-medium">Connect a Google Business Profile to generate schema</p>
                <p className="text-xs text-muted-foreground">
                  Add a location and we'll auto-build a Restaurant or LocalBusiness JSON-LD block from your real GBP data.
                </p>
              </div>
            )}
          </div>

          {/* Generated output */}
          <div className="space-y-3" data-testid="panel-generated-output">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-semibold">Your generated JSON-LD</span>
                {canGenerate ? (
                  <Badge
                    className="bg-green-500/15 text-green-600 border-green-500/30"
                    data-testid="badge-generator-ready"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Ready to install
                  </Badge>
                ) : (
                  <Badge variant="outline" data-testid="badge-generator-incomplete">
                    Pick a location above
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(generatedJsonString, "JSON-LD")}
                  disabled={!canGenerate}
                  data-testid="button-copy-json"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy JSON
                </Button>
                <Button
                  size="sm"
                  onClick={() => copy(generatedScriptTag, "Script tag")}
                  disabled={!canGenerate}
                  className="bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white"
                  data-testid="button-copy-script"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy &lt;script&gt; tag
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadSnippet}
                  disabled={!canGenerate}
                  data-testid="button-download-snippet"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download
                </Button>
              </div>
            </div>
            <pre
              className="text-[11px] overflow-x-auto leading-relaxed bg-muted/40 rounded p-3 border max-h-96"
              data-testid="text-generated-jsonld"
            >
              {generatedScriptTag}
            </pre>
          </div>

          {/* Install instructions */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm" data-testid="panel-install-instructions">
            <div className="flex items-center gap-2 font-semibold">
              <Code2 className="w-4 h-4 text-purple-500" />
              How to install
            </div>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Click <strong>Copy &lt;script&gt; tag</strong> above.</li>
              <li>
                Paste it into your website's HTML, ideally just before the closing{" "}
                <code className="text-xs">&lt;/head&gt;</code> tag, on every page (or in your global layout / theme
                header).
              </li>
              <li>
                Validate it at{" "}
                <a
                  href="https://search.google.com/test/rich-results"
                  target="_blank"
                  rel="noreferrer"
                  className="text-purple-500 underline hover:text-purple-600"
                  data-testid="link-rich-results-test"
                >
                  Google's Rich Results Test
                </a>
                {" "}and the{" "}
                <a
                  href="https://validator.schema.org/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-purple-500 underline hover:text-purple-600"
                  data-testid="link-schema-validator"
                >
                  Schema.org Validator
                </a>
                .
              </li>
              <li>
                Want the other 7 entity types (Organization, Service, FAQ, ImageObject, Person, WebPage, WebSite) all
                linked through <code className="text-xs">@graph</code> — and installed for you?{" "}
                <a href="#super-schema-card" className="text-purple-600 dark:text-purple-400 font-semibold underline hover:text-purple-700 dark:hover:text-purple-300" data-testid="link-super-schema-jump">
                  Upgrade to Super Schema
                </a>
                .
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Patents section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-purple-500" />
          <h2 className="text-2xl font-bold" data-testid="text-patents-title">
            What Google's patents actually say
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          These aren't blog theories — they're filed, granted patents from Google describing how DOM rendering and
          structured data flow into ranking decisions. Each one is a reason structured data belongs on your site.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {PATENTS.map((p) => (
            <Card
              key={p.id}
              className="border-purple-500/20 hover-elevate"
              data-testid={`card-patent-${p.id}`}
            >
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FileJson className="w-4 h-4 text-purple-500 shrink-0" />
                    <h3 className="font-semibold text-sm leading-tight" data-testid={`text-patent-name-${p.id}`}>
                      {p.name}
                    </h3>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono shrink-0" data-testid={`badge-patent-code-${p.id}`}>
                    {p.code}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.summary}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* What it does for ranking */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-purple-500" />
          <h2 className="text-2xl font-bold" data-testid="text-why-title">
            Why this moves the ranking needle
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Search, title: "Disambiguates your business", body: "Schema gives Google a single, declarative answer to 'who is this and what do they do?' — no scraping, no guessing." },
            { icon: Eye, title: "Survives the rendered DOM", body: "Per Google's patents, what counts is what's in the rendered DOM. JSON-LD is parsed during that render step every time." },
            { icon: Database, title: "Feeds the Knowledge Graph", body: "Structured properties (NAP, hours, category, geo) are exactly the fields the Knowledge Graph stores about local entities." },
            { icon: Sparkles, title: "Unlocks rich results", body: "Schema is the prerequisite for star ratings, FAQ snippets, sitelinks, and category-specific rich cards in the SERP." },
            { icon: Layers, title: "Reinforces NAP consistency", body: "Your sitewide schema becomes a third source of truth alongside your website and GBP — Google rewards three-way alignment." },
            { icon: Shield, title: "Hardens you against drift", body: "If a developer or theme update rewrites your visible HTML, the JSON-LD block keeps your ground-truth NAP intact." },
          ].map(({ icon: Icon, title, body }) => (
            <Card key={title} className="hover-elevate" data-testid={`card-benefit-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-5 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="font-semibold text-sm">{title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Bottom CTA — pushes Super Schema as the real fix */}
      <Card className="border-2 border-purple-500 bg-gradient-to-br from-purple-500/15 via-background to-fuchsia-500/15 shadow-xl shadow-purple-500/10">
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-bold" data-testid="text-cta-title">
                Stop settling. Get the real fix.
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              The free generator covers 1 of the 8 entity types Google looks for. Super Schema delivers all eight,
              hand-built and installed by us — the third pillar of local ranking, done right.
            </p>
          </div>
          <Button
            asChild
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow-lg shadow-purple-500/30 text-base font-semibold shrink-0"
            data-testid="button-jump-super-schema"
          >
            <a href="#super-schema-card">
              <Crown className="w-5 h-5 mr-2" />
              Get Super Schema — $999
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
